import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { TimePicker } from '@/components/ui/time-picker'
import { Badge } from '@/components/ui/badge'
import { OrderCreationLayout } from '@/components/order/OrderCreationLayout'
import { Clock, Activity, FileText, AlertCircle, CheckCircle2 } from 'lucide-react'
import { useOrderCreationStore } from '@/store/orderCreationStore'
import { useLocalization } from '@/contexts/LocalizationContext'
import {
  getOrderTemplateName,
  getLocalizedAppointmentSpecLabel,
  getIncludedServiceNameForAppointmentStep,
  getIncludedServiceDescriptionForAppointmentStep,
} from '@/utils/orderLocalization'
import { addMinutes, format, parseISO } from 'date-fns'
import type { AppointmentSchedule } from '@/store/orderCreationStore'

/**
 * Quick pick shortcuts rendered on every appointment card.
 * Each preset schedules the appointment at `minutes` from the current time
 * (both the date and the time are filled in).
 */
const quickPickPresets = [{ minutes: 5 }]

export function AppointmentConfigurationPage() {
  const navigate = useNavigate()
  const { locale, t } = useLocalization()
  const {
    selectedTemplate,
    clientProfileId,
    businessClientProfileId,
    appointments,
    setAppointments,
    setCurrentStep,
    getCreateOrderEntryPath,
  } = useOrderCreationStore()
  const orderSubjectProfileId = clientProfileId || businessClientProfileId

  const [localAppointments, setLocalAppointments] = useState<
    Array<{
      appointmentSpecId: string
      label: string
      date: Date | undefined
      time: string | undefined
      durationMinutes: number
      serviceSpecs: Array<{
        id: string
        orderIndex: number
        label: string
        serviceTemplate: {
          id: string
          name: string
          nameEn: string
          nameAr: string
          description?: string
        }
      }>
    }>
  >([])

  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    // Redirect if missing prerequisites
    if (!orderSubjectProfileId) {
      navigate(getCreateOrderEntryPath(), { replace: true })
      return
    }
    if (!selectedTemplate) {
      navigate('/create-order/templates')
      return
    }

    // Initialize appointments from template
    const initialAppointments = (selectedTemplate.appointmentSpecs || [])
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((spec) => {
        const existing = appointments.find(
          (apt) => apt.appointmentSpecId === spec.id
        )
        return {
          appointmentSpecId: spec.id,
          label: spec.label,
          date: existing
            ? parseISO(existing.scheduledStart)
            : undefined,
          time: existing
            ? format(parseISO(existing.scheduledStart), 'HH:mm')
            : undefined,
          durationMinutes: spec.durationMinutes,
          serviceSpecs: spec.serviceSpecs || [],
        }
      })

    setLocalAppointments(initialAppointments)
  }, [selectedTemplate, orderSubjectProfileId, appointments, navigate, getCreateOrderEntryPath])

  const handleDateChange = (index: number, date: Date | undefined) => {
    setValidationError(null)
    const updated = [...localAppointments]
    updated[index].date = date
    setLocalAppointments(updated)
  }

  const handleTimeChange = (index: number, time: string | undefined) => {
    setValidationError(null)
    const updated = [...localAppointments]
    updated[index].time = time
    setLocalAppointments(updated)
  }

  const handleQuickPick = (index: number, minutesFromNow: number) => {
    setValidationError(null)
    const target = addMinutes(new Date(), minutesFromNow)
    const updated = [...localAppointments]
    updated[index] = {
      ...updated[index],
      date: target,
      time: format(target, 'HH:mm'),
    }
    setLocalAppointments(updated)
  }

  const isAllAppointmentsValid = () => {
    return localAppointments.every((apt) => apt.date && apt.time)
  }

  const handleContinue = () => {
    // Validate all appointments have date and time
    const invalid = localAppointments.some(
      (apt) => !apt.date || !apt.time
    )
    if (invalid) {
      setValidationError(t('orderCreation.selectDateAndTimeForAll'))
      return
    }

    // Convert to AppointmentSchedule format
    const schedule: AppointmentSchedule[] = localAppointments.map((apt) => {
      const [hours, minutes] = apt.time!.split(':')
      const dateTime = new Date(apt.date!)
      dateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0)

      return {
        appointmentSpecId: apt.appointmentSpecId,
        scheduledStart: dateTime.toISOString(),
        label: apt.label,
      }
    })

    setAppointments(schedule)
    setCurrentStep(5)
    navigate('/create-order/beneficiary')
  }

  if (!selectedTemplate) {
    return null
  }

  const totalDuration = selectedTemplate.appointmentSpecs?.reduce(
    (acc, spec) => acc + spec.durationMinutes,
    0
  ) || 0

  return (
    <OrderCreationLayout
      titleKey="orderCreation.scheduleAppointments"
      descriptionKey="orderCreation.scheduleAppointmentsDescription"
      step={4}
      backPath="/create-order/templates"
    >
      <div className="max-w-4xl mx-auto">
        {/* Template Summary */}
        <Card className="mb-6 bg-muted/30">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  {t('orderCreation.selectedTemplate')}
                </p>
                <p className="font-semibold">{getOrderTemplateName(selectedTemplate, locale)}</p>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  <span>{totalDuration} {t('orderCreation.minutes')}</span>
                </div>
                <Badge variant="secondary">
                  {t('orderCreation.stepOf', { step: 4, total: 7 })}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Validation Error */}
        {validationError && (
          <Card className="mb-6 border-destructive bg-destructive/5">
            <CardContent className="py-4">
              <div className="flex items-center gap-3 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <p>{validationError}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Appointments */}
        <div className="space-y-4 mb-6">
          {localAppointments.map((appointment, index) => {
            const isComplete = appointment.date && appointment.time
            const spec = selectedTemplate.appointmentSpecs?.find(
              (s) => s.id === appointment.appointmentSpecId
            )
            const appointmentSubtitle =
              spec != null ? getLocalizedAppointmentSpecLabel(spec, locale) : ''
            const serviceCount = appointment.serviceSpecs.length
            return (
              <Card 
                key={appointment.appointmentSpecId}
                className={isComplete ? 'border-primary/30' : ''}
              >
                <CardHeader className="pb-3">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">
                          {t('orderCreation.appointmentNumber', { number: index + 1 })}
                        </CardTitle>
                        {isComplete && (
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      {appointmentSubtitle ? (
                        <CardDescription>{appointmentSubtitle}</CardDescription>
                      ) : null}
                    </div>
                    
                    {/* Date and Time Pickers */}
                    <div className="flex items-end gap-3 flex-wrap">
                      <div className="flex-shrink-0">
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                          {t('orderCreation.date')}
                        </label>
                        <DatePicker
                          date={appointment.date}
                          onDateChange={(date) => handleDateChange(index, date)}
                          minDate={new Date()}
                          className="w-[180px]"
                        />
                      </div>
                      <div className="flex-shrink-0">
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                          {t('orderCreation.time')}
                        </label>
                        <TimePicker
                          time={appointment.time}
                          onTimeChange={(time) => handleTimeChange(index, time)}
                          className="w-[120px]"
                        />
                      </div>
                      <Badge variant="outline" className="mb-0.5">
                        {appointment.durationMinutes} {t('orderCreation.minutes')}
                      </Badge>
                    </div>
                  </div>

                  {/* Quick picks: schedule relative to the current time */}
                  <div className="flex items-center gap-2 flex-wrap pt-2">
                    <span className="text-xs text-muted-foreground">
                      {t('orderCreation.quickPick')}
                    </span>
                    {quickPickPresets.map((preset) => (
                      <Button
                        key={preset.minutes}
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleQuickPick(index, preset.minutes)}
                      >
                        {t('orderCreation.quickPickInMinutes', {
                          minutes: preset.minutes,
                        })}
                      </Button>
                    ))}
                  </div>
                </CardHeader>

                {/* Services for this appointment — follows UI language (ar / en) */}
                {appointment.serviceSpecs.length > 0 && (
                  <CardContent className="pt-0">
                    <div className="border-t pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Activity className="h-4 w-4 text-muted-foreground" />
                        <h4 className="text-sm font-semibold">
                          {t('orderCreation.servicesIncluded')}
                        </h4>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {appointment.serviceSpecs
                          .sort((a, b) => a.orderIndex - b.orderIndex)
                          .map((serviceSpec) => {
                            const st = serviceSpec.serviceTemplate
                            const serviceName = getIncludedServiceNameForAppointmentStep(
                              st,
                              serviceSpec.label,
                              selectedTemplate,
                              locale,
                              serviceCount
                            )
                            const serviceDescription =
                              getIncludedServiceDescriptionForAppointmentStep(st, locale)
                            const textDir = locale === 'ar' ? 'rtl' : 'ltr'

                            return (
                              <div
                                key={serviceSpec.id}
                                className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg border border-border/60"
                              >
                                <div className="flex-1 min-w-0 space-y-2">
                                  {serviceName.trim() ? (
                                    <p
                                      dir={textDir}
                                      className="font-medium text-sm leading-snug"
                                    >
                                      {serviceName}
                                    </p>
                                  ) : null}
                                  {serviceDescription.trim() ? (
                                    <p
                                      dir={textDir}
                                      className="text-sm text-muted-foreground whitespace-pre-wrap break-words"
                                    >
                                      {serviceDescription}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            )
          })}
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          <Button 
            onClick={handleContinue}
            disabled={!isAllAppointmentsValid()}
            size="lg"
          >
            {t('orderCreation.continueToBeneficiarySelection')}
          </Button>
        </div>
      </div>
    </OrderCreationLayout>
  )
}
